import Analytics from '@/utils/analytics';
import ArrowLeft from '@assets/icons/arrow-left.svg';
import i18n from '@/utils/i18n';
import MenuIcon from '@assets/icons/menu.svg';
import React, { useState } from 'react';
import { ActionSheet } from '@/components/action-sheet';
import {
  APP_MODAL_ROUTES,
  EMPLOYEE_ROUTES,
  LOCATION_MANAGER_ROUTES,
} from '@/navigators/routes';
import { Button } from '@/components/button';
import { CompositeNavigationProp, RouteProp } from '@react-navigation/native';
import { EmployeeRole, RestaurantEmployeeFragment } from '@/types';
import {
  EmployeeStackParams,
  LocationManagerStackParams,
} from '@/navigators/types';
import { LayoutSize, useLayoutContext } from '@/contexts/layout-context';
import { ModalContainer } from '@/components/modal-container';
import { PageContainer } from '@/components/page-container';
import { RemoveEmployeeActionSheet } from '@/components/restaurant/remove-employee-action-sheet';
import { RestaurantAccountComponent } from './restaurant-account-component';
import { SCREEN_PADDING } from '@/styles';
import { StackNavigationProp } from '@react-navigation/stack';
import { StyleSheet, View } from 'react-native';
import { UpdateEmployeeFooter } from '@/components/restaurant/update-employee-footer';
import { useAuthenticatedUserContext } from '@/contexts/user-context';

interface Props {
  navigation: CompositeNavigationProp<
    StackNavigationProp<EmployeeStackParams, EMPLOYEE_ROUTES.Detail>,
    StackNavigationProp<
      LocationManagerStackParams,
      LOCATION_MANAGER_ROUTES.ViewEmployeeDetail
    >
  >;
  route:
    | RouteProp<EmployeeStackParams, EMPLOYEE_ROUTES.Detail>
    | RouteProp<
        LocationManagerStackParams,
        LOCATION_MANAGER_ROUTES.ViewEmployeeDetail
      >;
}

export const EmployeeDetailScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const {
    params: {
      businessEmployee,
      restaurantName,
      restaurantUUID,
      supplierUUID,
      useModalContainer,
    },
  } = route;
  const { selectedRestaurantRelationship, supplierEmployeeRole } =
    useAuthenticatedUserContext();
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [showRemoveSheet, setShowRemoveSheet] = useState(false);
  const [isMenuSheetOpen, setMenuSheetOpen] = useState(false);
  const { layoutSize } = useLayoutContext();

  const canUpdateEmployeeInfo = supplierEmployeeRole === EmployeeRole.Admin;
  const canUpdateEmployee =
    supplierEmployeeRole ||
    selectedRestaurantRelationship.employeeRole === EmployeeRole.Admin;

  const actionSheetActions = [];
  if (canUpdateEmployeeInfo) {
    actionSheetActions.push(i18n.t('update_employee'));
  }

  if (canUpdateEmployee) {
    actionSheetActions.push(i18n.t('remove_employee'));
  }

  const updateBusinessEmployee = (
    updatedEmployee: Partial<RestaurantEmployeeFragment>,
  ) => {
    navigation.setParams({
      businessEmployee: {
        ...businessEmployee,
        ...updatedEmployee,
      },
    });
  };

  const navigateToNotifications = () => {
    // @ts-ignore handle navigation from the location manager stack
    if (route.name === LOCATION_MANAGER_ROUTES.ViewEmployeeDetail) {
      navigation.navigate(LOCATION_MANAGER_ROUTES.ViewEmployeeNotifications, {
        useModalContainer,
        userUUID: businessEmployee.employee_id,
      });
    } else {
      navigation.navigate(EMPLOYEE_ROUTES.NotificationSettings, {
        useModalContainer,
        userUUID: businessEmployee.employee_id,
      });
    }
  };

  const children = (
    <>
      <View collapsable={false}>
        <RestaurantAccountComponent
          contactEmail={businessEmployee.employee_contact_email}
          contactPhoneNumber={businessEmployee.employee_contact_phone_number}
          containerStyle={styles.listContainer}
          employeeName={businessEmployee.employee_name}
          employeeUUID={businessEmployee.employee_id}
          ListFooterComponent={
            canUpdateEmployee ? (
              <UpdateEmployeeFooter
                businessEmployee={businessEmployee}
                onUpdate={updateBusinessEmployee}
              />
            ) : null
          }
          loginEmail={businessEmployee.employee_login_email}
          onPressNotifications={
            canUpdateEmployee ? navigateToNotifications : undefined
          }
        />
        <ActionSheet
          actions={actionSheetActions}
          dangerIndices={[1]}
          isVisible={actionSheetActions.length > 0 && showActionSheet}
          onAction={async (action) => {
            switch (action) {
              case i18n.t('update_employee'):
                navigation.navigate(APP_MODAL_ROUTES.UpdateEmployee, {
                  contactProtected: businessEmployee.employee_contact_protected,
                  employeeUUID: businessEmployee.employee_id,
                  initialEmail: businessEmployee.employee_contact_email ?? null,
                  initialLoginEmail:
                    businessEmployee.employee_login_email ?? null,
                  initialName: businessEmployee.employee_name,
                  initialPhoneNumber:
                    businessEmployee.employee_contact_phone_number,
                  onUpdate: updateBusinessEmployee,
                });
                break;
              case i18n.t('remove_employee'):
                Analytics.track(
                  'employee_detail.show_remove_modal_button.press',
                );
                setShowRemoveSheet(true);
                break;
            }

            setShowActionSheet(false);
          }}
          onClose={() => setShowActionSheet(false)}
          onModalHide={() => {
            setMenuSheetOpen(false);
          }}
          onModalShow={() => {
            setMenuSheetOpen(true);
          }}
        />
      </View>
      <RemoveEmployeeActionSheet
        businessName={restaurantName || selectedRestaurantRelationship.name}
        businessUUID={restaurantUUID || selectedRestaurantRelationship.uuid}
        employeeName={businessEmployee.employee_name}
        employeeUUID={businessEmployee.employee_id}
        isVisible={showRemoveSheet && !isMenuSheetOpen}
        onClose={(employeeRemoved: boolean) => {
          setShowRemoveSheet(false);
          if (employeeRemoved) {
            navigation.goBack();
          }
        }}
        supplierUUID={
          supplierUUID || selectedRestaurantRelationship.supplierUUID
        }
      />
    </>
  );

  return useModalContainer ? (
    <ModalContainer
      navigationHeaderProps={{
        actions:
          canUpdateEmployee && layoutSize === LayoutSize.SMALL
            ? [
                {
                  Icon: MenuIcon,
                  iconProps: {
                    testID: 'employee-menu-button',
                  },
                  onPress: () => {
                    Analytics.track('employee_detail.menu_button.press');
                    setShowActionSheet(true);
                  },
                },
              ]
            : undefined,
        BackButtonIcon: ArrowLeft,
        showBackButton: true,
      }}
    >
      <>
        {children}
        {canUpdateEmployee && layoutSize !== LayoutSize.SMALL ? (
          <Button
            onPress={() => {
              setShowRemoveSheet(true);
            }}
            style={styles.updateAccountButton}
            trackingData={{ name: 'account.remove_employee_button' }}
            type="tertiary"
          >
            {i18n.t('remove_employee')}
          </Button>
        ) : null}
      </>
    </ModalContainer>
  ) : (
    <PageContainer
      navigationHeaderProps={{
        actions: canUpdateEmployee
          ? [
              {
                Icon: MenuIcon,
                iconProps: {
                  testID: 'employee-menu-button',
                },
                onPress: () => {
                  Analytics.track('employee_detail.menu_button.press');
                  setShowActionSheet(true);
                },
              },
            ]
          : undefined,
        showBackButton: true,
      }}
    >
      {children}
    </PageContainer>
  );
};

const styles = StyleSheet.create({
  listContainer: {
    paddingHorizontal: SCREEN_PADDING,
  },
  updateAccountButton: {
    marginTop: 16,
  },
});
